import { z } from "zod";

export const ANALYSIS_AXES = {
  stability_challenge: { left: "安心・安定", right: "変化・挑戦", description: "安心できる土台と、新しい可能性のどちらを大切にしやすいか" },
  individual_relationship: { left: "自分の意思", right: "人との関係", description: "自分の考えと、周りとのつながりのどちらを大切にしやすいか" },
  intuition_deliberation: { left: "感覚で決める", right: "よく考える", description: "ひらめきと、情報を整理する時間のどちらを頼りにしやすいか" },
  plan_flexibility: { left: "計画どおり", right: "状況に合わせる", description: "先に決めた道筋と、その場での対応のどちらを選びやすいか" },
  outcome_process: { left: "得られた結果", right: "取り組む過程", description: "到達した成果と、そこまでの経験のどちらを大切にしやすいか" },
} as const;

export type AxisKey = keyof typeof ANALYSIS_AXES;

export const AVATAR_MIME_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;
export const MAX_AVATAR_BYTES = 150 * 1024;
export const AVATAR_OUTPUT_SIZE = 256;
export const QUESTION_IMAGE_MIME_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;
export const MAX_QUESTION_IMAGE_BYTES = 700 * 1024;
export const QUESTION_IMAGE_MAX_EDGE = 1280;
export const ATTRIBUTE_CELL_MIN = 20;

export const AGE_GROUPS = ["teens", "20s", "30s", "40s", "50s", "60plus", "undisclosed"] as const;
export const GENDERS = ["female", "male", "nonbinary", "other", "undisclosed"] as const;
export const REGIONS = ["hokkaido_tohoku", "kanto", "chubu", "kinki", "chugoku_shikoku", "kyushu_okinawa", "overseas", "undisclosed"] as const;
export const OCCUPATIONS = ["student", "employed", "self_employed", "caregiving", "homemaker", "retired", "other", "undisclosed"] as const;

export const LABELS = {
  ageGroup: { teens: "10代", "20s": "20代", "30s": "30代", "40s": "40代", "50s": "50代", "60plus": "60代以上", undisclosed: "回答しない" },
  gender: { female: "女性", male: "男性", nonbinary: "ノンバイナリー", other: "その他", undisclosed: "回答しない" },
  region: { hokkaido_tohoku: "北海道・東北", kanto: "関東", chubu: "中部", kinki: "近畿", chugoku_shikoku: "中国・四国", kyushu_okinawa: "九州・沖縄", overseas: "海外", undisclosed: "回答しない" },
  occupation: { student: "学生", employed: "会社員・公務員", self_employed: "自営業・経営", caregiving: "子育て・介護中", homemaker: "家事専業", retired: "退職後", other: "その他", undisclosed: "回答しない" },
} as const;

const tagName = z.string().trim().min(1, "タグを1つ以上指定してください").max(20).regex(/^[^,#\s]+$/u, "タグに空白・カンマ・#は使えません");

export const profileSchema = z.object({
  displayName: z.string().trim().min(1).max(30),
  bio: z.string().trim().max(160).default(""),
  ageGroup: z.enum(AGE_GROUPS),
  gender: z.enum(GENDERS),
  region: z.enum(REGIONS),
  occupation: z.enum(OCCUPATIONS),
  targetingOptOut: z.boolean().default(false),
  profilePublic: z.boolean().default(true),
  interestTags: z.array(tagName).max(10).default([]),
});

const targetSchema = z.object({
  ageGroups: z.array(z.enum(AGE_GROUPS)).max(6).default([]).refine((values) => !values.includes("undisclosed"), "「回答しない」は届け先に指定できません"),
  genders: z.array(z.enum(GENDERS)).max(4).default([]).refine((values) => !values.includes("undisclosed"), "「回答しない」は届け先に指定できません"),
  regions: z.array(z.enum(REGIONS)).max(7).default([]).refine((values) => !values.includes("undisclosed"), "「回答しない」は届け先に指定できません"),
  occupations: z.array(z.enum(OCCUPATIONS)).max(7).default([]).refine((values) => !values.includes("undisclosed"), "「回答しない」は届け先に指定できません"),
  tags: z.array(tagName).max(2).default([]),
});

export const questionSchema = z.object({
  body: z.string().trim().min(10, "問いは10文字以上で入力してください").max(240),
  tags: z.array(tagName).min(1).max(2),
  options: z.array(z.object({ label: z.string().trim().min(1).max(40), score: z.union([z.literal(-1), z.literal(0), z.literal(1)]) })).min(3).max(6),
  imageDataUrl: z.string().max(1_000_000).nullable().optional(),
  axis: z.enum(Object.keys(ANALYSIS_AXES) as [AxisKey, ...AxisKey[]]),
  analysisEnabled: z.boolean().default(false),
  target: targetSchema,
}).superRefine((value, context) => {
  if (new Set(value.tags.map((tag) => tag.toLowerCase())).size !== value.tags.length) context.addIssue({ code: "custom", path: ["tags"], message: "同じタグは指定できません" });
  if (new Set(value.options.map((option) => option.label)).size !== value.options.length) context.addIssue({ code: "custom", path: ["options"], message: "選択肢は異なる内容にしてください" });
  const scores = new Set(value.options.map((option) => option.score));
  if (!scores.has(-1) || !scores.has(1)) context.addIssue({ code: "custom", path: ["options"], message: "分析値には両端の値が必要です" });
});

const reasonTag = z.string().trim().min(1).max(20);
export const answerSchema = z.object({
  optionIndex: z.number().int().min(0).max(5),
  reason: z.string().trim().min(1, "理由を短く入力してください").max(280),
  reasonTags: z.array(reasonTag).min(1, "理由タグを1つ以上選んでください").max(3),
  confidence: z.union([z.literal(30), z.literal(60), z.literal(90)]),
});
export const revisionSchema = z.object({
  optionIndex: z.number().int().min(0).max(5),
  reason: z.string().trim().max(280).default(""),
  note: z.string().trim().min(1, "再考の理由を入力してください").max(280),
  confidence: z.union([z.literal(30), z.literal(60), z.literal(90)]).default(60),
});

export function scoreToParameter(scores: number[]): number | null {
  if (scores.length < 5) return null;
  const average = scores.reduce((sum, score) => sum + score, 0) / scores.length;
  return Math.round(((average + 1) / 2) * 100);
}

export function canRevealAttributeCells(counts: number[]): boolean {
  return counts.length >= 3 && counts.length <= 6 && counts.every((count) => Number.isInteger(count) && count >= ATTRIBUTE_CELL_MIN);
}

export function safeJsonArray(value: unknown): string[] {
  if (typeof value !== "string") return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === "string") : [];
  } catch {
    return [];
  }
}

export function normalizeTag(value: string): string {
  return value.trim().replace(/^#+/u, "").replace(/\s+/gu, " ");
}

export function splitTagInput(value: string): string[] {
  return value.split(/[、,\s]+/u).map(normalizeTag).filter(Boolean);
}
